// ============================================================================
// KASSA MANTIG'I — savat, summa, chegirma, yaxlitlash, modifikatorlar.
// Server (pos_order_create / pos_order_pay) bilan bir xil qoidalar: kassa
// ko'rsatgan summa server hisoblagan summaga teng bo'lishi kerak, aks holda
// online'da PRICE_MISMATCH bo'ladi.
// React'siz, sof funksiyalar — testlanadi (logic.test.ts).
// ============================================================================

import { decodeMono } from './bitmap';
import type { MonoBitmap } from './bitmap';
import { channelAvailable, channelMarkup, channelUnit } from '../lib/channels';
import type { ChannelRule } from '../lib/channels';

export type PayMethod = 'cash' | 'card' | 'click' | 'payme' | 'uzum' | 'transfer' | 'other' | 'aggregator';
export type OrderType = 'dine_in' | 'takeaway' | 'delivery';

export const PAY_METHODS: readonly { key: PayMethod; label: string }[] = [
  { key: 'cash', label: 'Naqd' },
  { key: 'card', label: 'Karta' },
  { key: 'click', label: 'Click' },
  { key: 'payme', label: 'Payme' },
  { key: 'uzum', label: 'Uzum' },
  { key: 'transfer', label: 'O‘tkazma' },
];

export const ORDER_TYPE_LABEL: Record<OrderType, string> = {
  takeaway: 'Olib ketish',
  dine_in: 'Zalda',
  delivery: 'Yetkazish',
};

export function payMethodLabel(m: string): string {
  return PAY_METHODS.find((p) => p.key === m)?.label ?? (m === 'other' ? 'Boshqa' : m === 'aggregator' ? 'Agregator orqali' : m);
}

// ---------------------------------------------------------------------------
// Katalog (pos_bootstrap)
// ---------------------------------------------------------------------------
export interface BootModifier {
  id: string;
  name: string;
  price: number;
  is_default: boolean;
}
export interface BootModifierGroup {
  id: string;
  name: string;
  min_select: number;
  max_select: number;
  modifiers: BootModifier[];
}
export interface BootProduct {
  id: string;
  category_id: string;
  name: string;
  short_name: string | null;
  price: number;
  color: string | null;
  image_url: string | null;
  group_name: string | null;
  variant_label: string | null;
  is_available: boolean;
  sort_order: number;
}
/** Kombo (Q-036): ega sozlagan tarkib. Variantlar: asosiysi birinchi, qolganlari — faqat ruxsat bo'lsa */
export interface BootComboOption {
  product_id: string;
  name: string;
  extra_price: number;
  is_default: boolean;
  modifiers: { id: string; name: string }[];
}
export interface BootComboSlot {
  id: string;
  qty: number;
  allow_swap: boolean;
  options: BootComboOption[];
}
export interface BootCombo {
  product_id: string;
  slots: BootComboSlot[];
}
export interface BootCategory {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  /** Fon rasmi (Q-054) */
  image_url?: string | null;
}
/** Guruh rasmi (Q-054): group — kichik harfda, chetdagi bo'sh joysiz */
export interface BootGroupImage {
  category_id: string;
  group: string;
  image_url: string;
}
export interface BootStaff {
  id: string;
  full_name: string;
  role_key: string;
  role_name: string;
  pin_length: number;
  pin_set_at: string;
  /** 2-parol: 'password' — kiritadi, 'setup' — o'rnatishi shart (Q-021) */
  password?: 'password' | 'setup' | null;
  password_set_at?: string | null;
  /** Boshqaruv paneliga kira oladi */
  panel?: boolean;
  permissions: { key: string; limit: number | null }[];
}
export interface BootTable {
  id: string;
  name: string;
  seats: number | null;
  /** Zal sxemasidagi joyi (Q-056): 12 ustunli to'r; null — joylashtirilmagan */
  pos_x?: number | null;
  pos_y?: number | null;
  shape?: 'square' | 'round' | 'rect' | null;
}
export interface Boot {
  /** Qurilma ega/menejer tomonidan tasdiqlanganmi (Q-021). Tasdiqlanmagan qurilmaga katalog berilmaydi. */
  approved?: boolean;
  /** Faqat tasdiqlanmagan qurilmada: qaysi filialga qo'shish mumkin */
  branches?: { id: string; name: string }[];
  server_time: string;
  business_date: string;
  tenant: { id: string; name: string; status: string; logo_url?: string | null };
  branch: { id: string; name: string; address: string | null; phone: string | null; timezone: string; currency: string };
  terminal: { id: string; name: string; kind: string; settings: Record<string, unknown>; approved?: boolean };
  modules: string[];
  settings: Record<string, unknown>;
  stations: { id: string; key: string; name: string; output: string }[];
  categories: BootCategory[];
  products: BootProduct[];
  /** Kilogrammda sotiladigan mahsulotlar va tez summalar (Q-072) */
  weight_products?: { product_id: string; amounts: number[] }[];
  modifier_groups: BootModifierGroup[];
  /** excluded — kategoriyadan keladigan guruh shu mahsulotda o'chirilgan */
  product_modifier_groups: { product_id: string; group_id: string; sort_order: number; excluded?: boolean }[];
  category_modifier_groups: { category_id: string; group_id: string; sort_order: number }[];
  zones: { id: string; name: string; tables: BootTable[] }[];
  staff: BootStaff[];
  shift: ShiftInfo | null;
  /** Kombolar tarkibi (eski server javobida bo'lmasligi mumkin) */
  combos?: BootCombo[];
  /** "Ko'p sotilganlar": mahsulot id'lari, ko'pidan kamiga */
  top_products?: string[];
  /** Savdo kanallari (Q-046): agregator narxi qoidasi, komissiyasiz */
  channels?: ChannelRule[];
  /** Guruh rasmlari (Q-054) */
  group_images?: BootGroupImage[];
}

export interface ShiftInfo {
  id: string;
  client_id: string;
  status: 'open' | 'closed';
  opened_at: string;
  closed_at?: string | null;
  staff_name?: string | null;
  opening_cash: number;
  closing_cash?: number | null;
  cash_in?: number;
  cash_out?: number;
  cash_sales?: number;
  card_sales?: number;
  other_sales?: number;
  by_method?: Record<string, number>;
  total_sales?: number;
  order_count?: number;
  void_count?: number;
  discount_total?: number;
  unpaid_count?: number;
  cash_refunds?: number;
  cash_expenses?: number;
  expected_cash?: number | null;
  difference?: number | null;
}

// ---------------------------------------------------------------------------
// Sozlamalar (server qiymatlari jsonb'dan)
// ---------------------------------------------------------------------------
export interface KassaSettings {
  orderTypes: OrderType[];
  askOrderType: boolean;
  paymentTiming: 'before' | 'after' | 'by_order_type';
  servicePercent: number;
  roundingStep: number;
  receiptHeader: string;
  receiptFooter: string;
  /** Chek oxirida “ONE POS — onepos.uz” qatori (receipt.brand, standart — yoqilgan) */
  receiptBrand: boolean;
  /** Chek ko'rinishi: nima chiqadi va restoran nomi qanday (receipt.*) */
  receipt: ReceiptDesign;
  /** Stol shundan uzoq band bo'lsa kassada ajratiladi (hall.long_minutes) */
  tableLongMinutes: number;
  /** Kassa zal sxemasidan boshlanadi (hall.start, Q-056) */
  hallStart: boolean;
  /** Kassada chiqadigan to'lov turlari (pos.pay_methods) */
  payMethods: PayMethod[];
  /** Kategoriya tugmalarida fon rasmi (pos.category_images, Q-054) — ega o'chira oladi */
  categoryImages: boolean;
  /** Bekor qilingan taom — sklad (order.void_stock, Q-061) */
  voidStock: VoidStockSetting;
  /** Hisob chekidan keyin hisob qulflanadi (hall.bill_lock, Q-062) */
  billLock: boolean;
  /** Ofitsiant telefonidan to'lov (hall.waiter_payment, Q-067) */
  waiterPayment: 'kassa' | 'waiter';
  /** Ofitsiantlar stollari (waiter.tables, Q-070): o'z stoli / umumiy / faqat o'ziniki */
  waiterTables: 'own' | 'shared' | 'own_only';
  /** Mehmonlar soni (hall.ask_guests): so'ralmaydi / so'raladi / majburiy */
  askGuests: 'off' | 'ask' | 'required';
  /** "Taom tayyor" xabari ofitsiantga (waiter.ready_notify) */
  readyNotify: boolean;
}

export type VoidStockMode = 'auto' | 'return' | 'waste';
export type VoidStockSetting = VoidStockMode | 'ask';

export function readSettings(s: Record<string, unknown>): KassaSettings {
  const types = Array.isArray(s['flow.order_types'])
    ? (s['flow.order_types'] as unknown[]).filter((t): t is OrderType => t === 'dine_in' || t === 'takeaway' || t === 'delivery')
    : [];
  const timing = s['flow.payment_timing'];
  return {
    orderTypes: types.length ? types : ['takeaway'],
    askOrderType: s['pos.ask_order_type'] === true,
    paymentTiming: timing === 'after' || timing === 'by_order_type' ? timing : 'before',
    servicePercent: typeof s['flow.service_charge_percent'] === 'number' ? (s['flow.service_charge_percent'] as number) : 0,
    roundingStep: Number(s['branch.cash_rounding'] ?? 0) || 0,
    receiptHeader: typeof s['receipt.header'] === 'string' ? (s['receipt.header'] as string) : '',
    receiptFooter: typeof s['receipt.footer'] === 'string' ? (s['receipt.footer'] as string) : '',
    receiptBrand: s['receipt.brand'] !== false,
    receipt: readReceiptDesign(s),
    tableLongMinutes: typeof s['hall.long_minutes'] === 'number' ? (s['hall.long_minutes'] as number) : 60,
    hallStart: s['hall.start'] === true,
    payMethods: readPayMethods(s['pos.pay_methods']),
    categoryImages: s['pos.category_images'] !== false,
    voidStock: (['auto', 'return', 'waste', 'ask'] as const).find((v) => v === s['order.void_stock']) ?? 'auto',
    billLock: s['hall.bill_lock'] !== false,
    waiterPayment: s['hall.waiter_payment'] === 'waiter' ? 'waiter' : 'kassa',
    waiterTables: s['waiter.tables'] === 'shared' || s['waiter.tables'] === 'own_only' ? s['waiter.tables'] : 'own',
    askGuests: s['hall.ask_guests'] === 'off' || s['hall.ask_guests'] === 'required' ? s['hall.ask_guests'] : 'ask',
    readyNotify: s['waiter.ready_notify'] === true,
  };
}

/** Hisob cheki chiqqan (prechek, Q-062) */
export function billPrinted(o: Pick<LocalOrder, 'bill_printed_at' | 'status' | 'is_paid'>): boolean {
  return Boolean(o.bill_printed_at) && o.status !== 'voided' && !o.is_paid;
}

/** Hisob qulflangan: prechek chiqqan va restoran sozlamasi yoqilgan */
export function billLocked(o: Pick<LocalOrder, 'bill_printed_at' | 'status' | 'is_paid'>, s: Pick<KassaSettings, 'billLock'>): boolean {
  return s.billLock && billPrinted(o);
}

/** Prechekdan keyin summa o'zgargan (qulf o'chiq bo'lsa) — yangisini chiqarish kerak */
export function billStale(o: Pick<LocalOrder, 'bill_printed_at' | 'bill_total' | 'total' | 'status' | 'is_paid'>): boolean {
  return billPrinted(o) && o.bill_total != null && o.bill_total !== o.total;
}

/** Taom skladga qaytadimi: usul va taom holati bo'yicha (server qoidasi bilan bir xil) */
export function itemReturns(mode: VoidStockMode, status: string | undefined): boolean {
  return mode === 'return' ? true : mode === 'waste' ? false : !status || status === 'new';
}

function readPayMethods(v: unknown): PayMethod[] {
  const all = PAY_METHODS.map((m) => m.key);
  if (!Array.isArray(v)) return all;
  const picked = all.filter((k) => v.includes(k));
  return picked.length ? picked : all;
}

/** Bu buyurtma turida to'lov keyin olinishi mumkinmi (zal: avval ovqat) */
export function canPayLater(settings: KassaSettings, type: OrderType): boolean {
  if (settings.paymentTiming === 'after') return true;
  if (settings.paymentTiming === 'by_order_type') return type === 'dine_in';
  return type === 'dine_in';
}

// ---------------------------------------------------------------------------
// Savdo oynasidagi kartalar: bir xil "guruh" nomli o'lchamlar (Coca-Cola 0,5 / 1 / 1,5 L)
// bitta kartada; bosilganda o'lcham so'raladi. Guruh — shu kategoriya ichida, kamida 2 ta.
// ---------------------------------------------------------------------------
export type CatalogTile =
  | { kind: 'product'; product: BootProduct }
  | { kind: 'group'; key: string; name: string; products: BootProduct[] };

const groupKey = (p: Pick<BootProduct, 'category_id' | 'group_name'>): string | null => {
  const g = (p.group_name ?? '').trim().toLowerCase();
  return g ? `${p.category_id}|${g}` : null;
};

/** Kartalar tartibi saqlanadi: guruh birinchi a'zosi turgan joyda chiqadi */
export function catalogTiles(products: readonly BootProduct[], group = true): CatalogTile[] {
  if (!group) return products.map((product) => ({ kind: 'product', product }));
  const members = new Map<string, BootProduct[]>();
  for (const p of products) {
    const k = groupKey(p);
    if (k) members.set(k, [...(members.get(k) ?? []), p]);
  }
  const out: CatalogTile[] = [];
  const done = new Set<string>();
  for (const p of products) {
    const k = groupKey(p);
    const list = k ? members.get(k)! : [];
    if (!k || list.length < 2) {
      out.push({ kind: 'product', product: p });
    } else if (!done.has(k)) {
      done.add(k);
      out.push({ kind: 'group', key: k, name: p.group_name!.trim(), products: list });
    }
  }
  return out;
}

/** Guruh rasmlari: kalit — catalogTiles dagi guruh kaliti ("<kategoriya>|<guruh>") */
export function groupImageMap(images: readonly BootGroupImage[] | undefined): Map<string, string> {
  return new Map((images ?? []).filter((g) => g.image_url).map((g) => [`${g.category_id}|${g.group.trim().toLowerCase()}`, g.image_url]));
}

/** Guruh kartasi: sotsa bo'ladigan o'lchamlarning eng arzon narxi (yo'q bo'lsa null); rasm — guruh rasmi, bo'lmasa birinchi o'lchamniki */
export function groupInfo(
  products: readonly BootProduct[],
  groupImage: string | null = null,
): { available: boolean; minPrice: number | null; image: string | null } {
  const sellable = products.filter((p) => p.is_available && p.price > 0);
  return {
    available: products.some((p) => p.is_available),
    minPrice: sellable.length ? Math.min(...sellable.map((p) => p.price)) : null,
    image: groupImage ?? products.find((p) => p.image_url)?.image_url ?? null,
  };
}

/** O'lcham tugmasidagi yozuv: "0,5 L"; o'lcham yozilmagan bo'lsa — nomi */
export function variantName(p: Pick<BootProduct, 'name' | 'short_name' | 'variant_label'>): string {
  return (p.variant_label ?? '').trim() || p.short_name || p.name;
}

// ---------------------------------------------------------------------------
// Modifikator guruhlari: mahsulotniki + kategoriyaniki − mahsulotda o'chirilganlari, takrorsiz
// ---------------------------------------------------------------------------
export function groupsForProduct(boot: Pick<Boot, 'modifier_groups' | 'product_modifier_groups' | 'category_modifier_groups'>, product: Pick<BootProduct, 'id' | 'category_id'>): BootModifierGroup[] {
  const byId = new Map(boot.modifier_groups.map((g) => [g.id, g]));
  const own = boot.product_modifier_groups.filter((l) => l.product_id === product.id);
  const off = new Set(own.filter((l) => l.excluded).map((l) => l.group_id));
  const links = [
    ...own.filter((l) => !l.excluded).map((l) => ({ id: l.group_id, o: l.sort_order })),
    ...boot.category_modifier_groups.filter((l) => l.category_id === product.category_id).map((l) => ({ id: l.group_id, o: 1000 + l.sort_order })),
  ].sort((a, b) => a.o - b.o);
  const seen = new Set<string>();
  const out: BootModifierGroup[] = [];
  for (const l of links) {
    const g = byId.get(l.id);
    if (!g || seen.has(g.id) || off.has(g.id) || g.modifiers.length === 0) continue;
    seen.add(g.id);
    out.push(g);
  }
  return out;
}

/** Tanlov guruh qoidasiga mosmi: null = to'g'ri, aks holda xato matni */
export function checkGroup(group: BootModifierGroup, selected: readonly string[]): string | null {
  const n = selected.filter((id) => group.modifiers.some((m) => m.id === id)).length;
  if (n < group.min_select) return `${group.name}: kamida ${group.min_select} ta tanlang`;
  if (group.max_select > 0 && n > group.max_select) return `${group.name}: ko‘pi bilan ${group.max_select} ta`;
  return null;
}

// ---------------------------------------------------------------------------
// Savat
// ---------------------------------------------------------------------------
export interface CartModifier {
  id: string;
  name: string;
  price: number;
}
/** Kombo tarkibidagi bitta mahsulot: qaysi bo'limdan, qaysi variant (soni — bitta kombo uchun) */
export interface CartComboPart {
  slot_id: string;
  product_id: string;
  name: string;
  qty: number;
  /** Ustama narx (asosiy variantda 0) */
  extra: number;
  /** Ega biriktirgan qo'shimchalar nomi */
  modifiers: string[];
}

export interface CartLine {
  key: string;
  product_id: string | null;
  name: string;
  price: number;
  qty: number;
  modifiers: CartModifier[];
  is_custom?: boolean;
  /** Kombo bo'lsa — tarkibi (kassir qo'shimcha qo'sha olmaydi: modifiers bo'sh) */
  combo?: CartComboPart[];
  /** Kilogrammda (Q-072): og'irlik, gramm; price — 1 kg narxi; qty — doim 1 */
  weight_g?: number;
  /** "Summa bo'yicha" — mijoz aytgan summa (qator summasi aynan shu) */
  amount?: number | null;
}

export function lineKey(productId: string, modifiers: readonly CartModifier[]): string {
  return `${productId}|${modifiers.map((m) => m.id).sort().join(',')}`;
}

export function comboLineKey(productId: string, parts: readonly CartComboPart[]): string {
  return `combo|${productId}|${parts.map((p) => `${p.slot_id}:${p.product_id}`).join(',')}`;
}

// ---------------------------------------------------------------------------
// Kilogrammda sotish (Q-072). Server bilan bir xil: summa = 1 kg narxi × gramm / 1000 (yaxlitlangan);
// "summa bo'yicha" — gramm butun songa yaxlitlanadi, summa — mijoz aytgani.
// ---------------------------------------------------------------------------
export const WEIGHT_GRAMS = [100, 250, 500, 1000] as const;
export const MAX_WEIGHT_G = 100_000;

export function amountForGrams(kgPrice: number, grams: number): number {
  return Math.round((kgPrice * grams) / 1000);
}

export function gramsForAmount(kgPrice: number, amount: number): number {
  if (kgPrice <= 0) return 0;
  return Math.round((amount * 1000) / kgPrice);
}

/** "235 g" yoki "1,25 kg" */
export function fmtWeight(grams: number): string {
  if (grams < 1000) return `${grams} g`;
  const kg = (grams / 1000).toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return `${kg.replace('.', ',')} kg`;
}

/** Chekda: "0,235 kg" (doim kg, 3 xona) */
export function fmtKg(grams: number): string {
  return `${(grams / 1000).toFixed(3).replace('.', ',')} kg`;
}

export function weightLineTotal(l: Pick<CartLine, 'price' | 'weight_g' | 'amount'>): number {
  return l.amount ?? amountForGrams(l.price, l.weight_g ?? 0);
}

/** Kilogrammlik qator: har tortish — alohida qator */
export function addWeightToCart(
  lines: readonly CartLine[],
  product: Pick<BootProduct, 'id' | 'name' | 'price'>,
  grams: number,
  amount: number | null,
  key: string,
): CartLine[] {
  return [...lines, { key: `kg|${product.id}|${key}`, product_id: product.id, name: product.name, price: product.price, qty: 1, modifiers: [], weight_g: grams, amount }];
}

export function lineUnit(line: CartLine): number {
  if (line.weight_g) return weightLineTotal(line);
  return line.price + line.modifiers.reduce((s, m) => s + m.price, 0) + (line.combo ?? []).reduce((s, p) => s + p.extra, 0);
}

export function lineTotal(line: CartLine): number {
  return lineUnit(line) * line.qty;
}

export function addToCart(lines: readonly CartLine[], product: Pick<BootProduct, 'id' | 'name' | 'price'>, modifiers: readonly CartModifier[]): CartLine[] {
  const key = lineKey(product.id, modifiers);
  if (lines.some((l) => l.key === key)) {
    return lines.map((l) => (l.key === key ? { ...l, qty: Math.min(999, l.qty + 1) } : l));
  }
  return [...lines, { key, product_id: product.id, name: product.name, price: product.price, qty: 1, modifiers: [...modifiers] }];
}

export function addComboToCart(lines: readonly CartLine[], product: Pick<BootProduct, 'id' | 'name' | 'price'>, parts: readonly CartComboPart[]): CartLine[] {
  const key = comboLineKey(product.id, parts);
  if (lines.some((l) => l.key === key)) {
    return lines.map((l) => (l.key === key ? { ...l, qty: Math.min(999, l.qty + 1) } : l));
  }
  return [...lines, { key, product_id: product.id, name: product.name, price: product.price, qty: 1, modifiers: [], combo: parts.map((p) => ({ ...p })) }];
}

export function addCustom(lines: readonly CartLine[], name: string, price: number, uid: string): CartLine[] {
  return [...lines, { key: `custom|${uid}`, product_id: null, name: name.trim() || 'Erkin narx', price, qty: 1, modifiers: [], is_custom: true }];
}

export function changeQty(lines: readonly CartLine[], key: string, delta: number): CartLine[] {
  return lines
    // Kilogrammlik qator: faqat olib tashlanadi (soni doim 1)
    .map((l) => (l.key === key ? { ...l, qty: l.weight_g ? (delta < 0 ? 0 : 1) : Math.min(999, l.qty + delta) } : l))
    .filter((l) => l.qty > 0);
}

export function cartCount(lines: readonly CartLine[]): number {
  return lines.reduce((s, l) => s + l.qty, 0);
}

export function cartSubtotal(lines: readonly CartLine[]): number {
  return lines.reduce((s, l) => s + lineTotal(l), 0);
}

/** Katalog yangilangach narxlarni yangilash (menyudan olib tashlangan qator qoladi) */
export function repriceCart(lines: readonly CartLine[], boot: Pick<Boot, 'products' | 'modifier_groups' | 'combos'>): CartLine[] {
  const prices = new Map(boot.products.map((p) => [p.id, p.price]));
  const mods = new Map(boot.modifier_groups.flatMap((g) => g.modifiers).map((m) => [m.id, m.price]));
  const combos = new Map((boot.combos ?? []).map((c) => [c.product_id, c]));
  return lines.map((l) =>
    l.is_custom || !l.product_id
      ? l
      : l.weight_g
        ? // Kilogrammlik: "summa bo'yicha"da summa saqlanadi — gramm yangi narxdan
          (() => {
            const price = prices.get(l.product_id!) ?? l.price;
            return { ...l, price, weight_g: l.amount ? Math.max(1, gramsForAmount(price, l.amount)) : l.weight_g };
          })()
      : {
          ...l,
          price: prices.get(l.product_id) ?? l.price,
          modifiers: l.modifiers.map((m) => ({ ...m, price: mods.get(m.id) ?? m.price })),
          ...(l.combo ? { combo: repriceParts(l.combo, combos.get(l.product_id)) } : {}),
        },
  );
}

function repriceParts(parts: readonly CartComboPart[], combo: BootCombo | undefined): CartComboPart[] {
  if (!combo) return [...parts];
  return parts.map((p) => {
    const o = combo.slots.find((s) => s.id === p.slot_id)?.options.find((x) => x.product_id === p.product_id);
    return o ? { ...p, extra: o.extra_price, name: o.name, modifiers: o.modifiers.map((m) => m.name) } : p;
  });
}

// ---------------------------------------------------------------------------
// Kombo (Q-036): tarkibni ega sozlaydi, kassir faqat ruxsat etilgan variantni tanlaydi
// ---------------------------------------------------------------------------
export function comboFor(boot: Pick<Boot, 'combos'>, productId: string): BootCombo | undefined {
  return (boot.combos ?? []).find((c) => c.product_id === productId && c.slots.length > 0);
}

export function comboPart(slot: BootComboSlot, option: BootComboOption): CartComboPart {
  return {
    slot_id: slot.id,
    product_id: option.product_id,
    name: option.name,
    qty: slot.qty,
    extra: option.is_default ? 0 : option.extra_price,
    modifiers: option.modifiers.map((m) => m.name),
  };
}

/** Bo'limda tanlab bo'ladigan variantlar: almashtirish o'chiq bo'lsa — faqat asosiysi */
export function slotOptions(slot: BootComboSlot): BootComboOption[] {
  const def = slot.options.filter((o) => o.is_default);
  return slot.allow_swap ? [...def, ...slot.options.filter((o) => !o.is_default)] : def.slice(0, 1);
}

/** Mahsulot sotuvda bormi ("tugadi" emas). Katalogda yo'q (arxivdagi) asosiy mahsulot — sotiladi deb olinadi. */
function availableMap(products: readonly Pick<BootProduct, 'id' | 'is_available'>[]): (id: string) => boolean {
  const m = new Map(products.map((p) => [p.id, p.is_available]));
  return (id) => m.get(id) ?? true;
}

/** Har bo'lim uchun: asosiysi bor bo'lsa — u, aks holda birinchi bor variant; hech biri yo'q bo'lsa — null */
export function comboStartParts(combo: BootCombo, products: readonly Pick<BootProduct, 'id' | 'is_available'>[]): (CartComboPart | null)[] {
  const ok = availableMap(products);
  return combo.slots.map((s) => {
    const opt = slotOptions(s).find((o) => ok(o.product_id));
    return opt ? comboPart(s, opt) : null;
  });
}

/** Kassada: kombo sotiladimi (har bo'limda kamida bitta bor variant) */
export function comboAvailable(combo: BootCombo, products: readonly Pick<BootProduct, 'id' | 'is_available'>[]): boolean {
  return comboStartParts(combo, products).every((p) => p !== null);
}

/** Tanlov oynasi kerakmi: kamida bitta bo'limda almashtirish varianti bo'lsa (aks holda bir bosishda savatga) */
export function comboNeedsChoice(combo: BootCombo): boolean {
  return combo.slots.some((s) => slotOptions(s).length > 1);
}

/** "Ko'p sotilganlar": katalogdagi (faol) mahsulotlar, server tartibida */
export function topProducts(boot: Pick<Boot, 'products' | 'top_products'>): BootProduct[] {
  const byId = new Map(boot.products.map((p) => [p.id, p]));
  return (boot.top_products ?? []).map((id) => byId.get(id)).filter((p): p is BootProduct => Boolean(p));
}

// ---------------------------------------------------------------------------
// Summa: server bilan bir xil
// ---------------------------------------------------------------------------
export interface Totals {
  subtotal: number;
  discount: number;
  service: number;
  /** Yaxlitlashdan oldin */
  pre: number;
  /** Faqat to'liq naqd to'lovda (manfiy yoki 0) */
  rounding: number;
  /** Naqd bilan to'lansa */
  cashTotal: number;
  /** Boshqa usul (yoki aralash) bilan to'lansa */
  total: number;
}

export function computeTotals(input: {
  subtotal: number;
  discount: number;
  orderType: OrderType;
  servicePercent: number;
  roundingStep: number;
}): Totals {
  const subtotal = Math.max(0, Math.round(input.subtotal));
  const discount = Math.min(subtotal, Math.max(0, Math.round(input.discount)));
  const service = input.orderType === 'dine_in' && input.servicePercent > 0
    ? Math.round(((subtotal - discount) * input.servicePercent) / 100)
    : 0;
  const pre = subtotal - discount + service;
  const rounding = input.roundingStep > 0 ? -(pre % input.roundingStep) : 0;
  return { subtotal, discount, service, pre, rounding, cashTotal: pre + rounding, total: pre };
}

/** Chegirma foizi (server: discount*100/subtotal, 2 xona) */
export function discountPercent(discount: number, subtotal: number): number {
  if (discount <= 0) return 0;
  return Math.round((discount * 100 * 100) / Math.max(subtotal, 1)) / 100;
}

/** Foizdan summa (so'mga yaxlit, limitdan oshmasin) */
export function discountFromPercent(percent: number, subtotal: number): number {
  return Math.floor((subtotal * percent) / 100);
}

/**
 * Keyin to'lash: qolgan summa. Server qoidasi: buyurtma yaxlitlanmagan bo'lsa va
 * hamma to'lov naqd bo'lsa — jami pastga yaxlitlanadi.
 */
export function payDue(input: {
  total: number;
  alreadyRounded: boolean;
  hasMismatch: boolean;
  allCash: boolean;
  roundingStep: number;
}): number {
  if (input.allCash && input.roundingStep > 0 && !input.alreadyRounded && !input.hasMismatch) {
    return input.total - (input.total % input.roundingStep);
  }
  return input.total;
}

// ---------------------------------------------------------------------------
// Naqd: tez tugmalar (aniq summa + keyingi yaxlit kupyuralar)
// ---------------------------------------------------------------------------
export function quickCash(amount: number): number[] {
  if (amount <= 0) return [];
  const out = new Set<number>([amount]);
  for (const step of [1000, 5000, 10000, 50000, 100000, 200000]) {
    const v = Math.ceil(amount / step) * step;
    if (v > amount) out.add(v);
    if (out.size >= 5) break;
  }
  return [...out].sort((a, b) => a - b).slice(0, 5);
}

// ---------------------------------------------------------------------------
// Huquqlar
// ---------------------------------------------------------------------------
export interface PermSet {
  has: (key: string) => boolean;
  limit: (key: string) => number | null;
}

export function permSet(perms: readonly { key: string; limit: number | null }[]): PermSet {
  const map = new Map(perms.map((p) => [p.key, p.limit]));
  return {
    has: (k) => map.has(k),
    limit: (k) => {
      const v = map.get(k);
      return v === undefined || v === null ? null : Number(v);
    },
  };
}

// ---------------------------------------------------------------------------
// Serverga yuboriladigan buyurtma
// ---------------------------------------------------------------------------
export interface PaymentInput {
  client_id: string;
  method: PayMethod;
  amount: number;
  cash_received?: number;
  change_amount?: number;
}

export function orderItems(lines: readonly CartLine[]) {
  return lines.map((l) =>
    l.is_custom || !l.product_id
      ? { is_custom: true, name: l.name, unit_price: l.price, qty: l.qty }
      : l.weight_g
        ? { product_id: l.product_id, qty: 1, weight_g: l.weight_g, ...(l.amount ? { amount: l.amount } : {}) }
      : l.combo
        ? { product_id: l.product_id, qty: l.qty, combo: l.combo.map((p) => ({ slot_id: p.slot_id, product_id: p.product_id })) }
        : { product_id: l.product_id, qty: l.qty, modifiers: l.modifiers.map((m) => m.id) },
  );
}

/** Kombo tarkibi buyurtma qatorida (soni — butun qator uchun) */
export function lineComponents(l: CartLine): OrderItemComponent[] | undefined {
  return l.combo?.map((p) => ({ product_id: p.product_id, name: p.name, qty: p.qty * l.qty, modifiers: [...p.modifiers] }));
}

/** Savat qatori → buyurtma qatori (kassadagi nusxa: internetsiz chek va ro'yxat uchun) */
export function lineToItem(l: CartLine): OrderItemJson {
  if (l.weight_g) {
    const total = weightLineTotal(l);
    return { product_id: l.product_id, name: l.name, qty: 1, unit_price: total, modifiers_price: 0, line_total: total, is_custom: false, modifiers: [], weight_g: l.weight_g, kg_price: l.price };
  }
  return {
    product_id: l.product_id,
    name: l.name,
    qty: l.qty,
    unit_price: l.price,
    modifiers_price: lineUnit(l) - l.price,
    line_total: lineUnit(l) * l.qty,
    is_custom: Boolean(l.is_custom),
    modifiers: l.modifiers.map((m) => ({ id: m.id, name: m.name, price: m.price })),
    ...(l.combo ? { is_combo: true, components: lineComponents(l) } : {}),
  };
}

// ---------------------------------------------------------------------------
// Server buyurtmasi (app.order_json) va lokal nusxa
// ---------------------------------------------------------------------------
export interface OrderItemJson {
  id?: string;
  product_id: string | null;
  name: string;
  qty: number;
  unit_price: number;
  modifiers_price: number;
  line_total: number;
  is_custom: boolean;
  note?: string | null;
  status?: string;
  /** Bekor qilingan taom sababi (Q-060) */
  void_reason?: string | null;
  modifiers: { id: string; name: string; price: number }[];
  /** Kombo: tarkibi (narxi kombo qatorida) */
  is_combo?: boolean;
  components?: OrderItemComponent[] | null;
  /** Kilogrammda (Q-072): og'irlik va 1 kg narxi */
  weight_g?: number | null;
  kg_price?: number | null;
  /** To'langan buyurtmadan qaytarilgan soni (Q-077) */
  refunded_qty?: number;
}

export interface OrderItemComponent {
  product_id?: string | null;
  name: string;
  qty: number;
  status?: string;
  modifiers: string[];
}

export interface OrderJson {
  id: string | null;
  client_id: string;
  shift_id?: string | null;
  staff_id?: string | null;
  staff_name: string | null;
  table_id: string | null;
  table_name: string | null;
  daily_number: number | null;
  business_date: string;
  order_type: OrderType;
  status: 'new' | 'preparing' | 'ready' | 'served' | 'voided';
  subtotal: number;
  discount_amount: number;
  discount_reason?: string | null;
  service_charge: number;
  rounding: number;
  total: number;
  expected_total: number | null;
  has_price_mismatch: boolean;
  is_paid: boolean;
  note?: string | null;
  source?: string;
  /** Mehmonlar soni (zal) */
  guests?: number | null;
  channel_id?: string | null;
  channel_name?: string | null;
  external_no?: string | null;
  created_at_local: string;
  voided_at?: string | null;
  void_reason?: string | null;
  /** Hisob cheki (prechek, Q-062): qachon, qancha summaga, nechanchi marta */
  bill_printed_at?: string | null;
  bill_total?: number | null;
  bill_count?: number;
  /** Hisob cheki kassa printerida (ofitsiant, Q-067): navbatda, chiqdi yoki chiqmadi */
  bill_print?: 'pending' | 'printed' | 'failed' | null;
  /** Stol egasi — ofitsiant (Q-067); kassada ochilgan buyurtmada yo'q */
  waiter_id?: string | null;
  waiter_name?: string | null;
  /** Boshqa ofitsiant stoli, "faqat o'ziniki" rejimida (Q-070): ichidagisi va summasi berilmaydi */
  hidden?: boolean;
  paid: number;
  refunded: number;
  items: OrderItemJson[];
  payments: { method: string; amount: number; cash_received?: number | null; change_amount?: number | null }[];
}

/** Kassadagi nusxa: serverdan kelgan yoki hali yuborilmagan */
export interface LocalOrder extends OrderJson {
  local_no?: number;
  sync: 'synced' | 'pending' | 'dead';
  error?: string;
}

/** Ko'rsatiladigan raqam: server raqami yoki lokal "L-3" */
export function orderNo(o: Pick<LocalOrder, 'daily_number' | 'local_no'>): string {
  if (o.daily_number) return `#${o.daily_number}`;
  return o.local_no ? `L-${o.local_no}` : '—';
}

// ---------------------------------------------------------------------------
// Oshxona ekrani: vaqt
// ---------------------------------------------------------------------------
/** O'tgan vaqt: "4:05" */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** Kechikish darajasi: kutilgan tayyorlash vaqtiga nisbatan */
export function lateness(elapsedMs: number, prepMinutes: number): 'ok' | 'warn' | 'late' {
  const expected = Math.max(prepMinutes, 3) * 60_000;
  if (elapsedMs <= expected) return 'ok';
  if (elapsedMs <= expected * 2) return 'warn';
  return 'late';
}

/** Chek yozuvlari guruhlari (Q-065): har biri — kattalik va qalinlik */
export type ReceiptGroup = 'top' | 'name' | 'contacts' | 'no' | 'info' | 'items' | 'sums' | 'total' | 'footer';
export type ReceiptSize = 1 | 2 | 3;
export interface LineStyle {
  size: ReceiptSize;
  bold: boolean;
}

/** Guruhlar tartibi, nomi va standart ko'rinishi (hozirgi chek bilan aynan bir xil) */
export const RECEIPT_GROUPS: readonly { key: ReceiptGroup; label: string; hint: string; size: ReceiptSize; bold: boolean }[] = [
  { key: 'top', label: 'Tepadagi xabar', hint: 'Restoran nomidan oldin', size: 1, bold: false },
  { key: 'name', label: 'Restoran nomi', hint: '', size: 2, bold: true },
  { key: 'contacts', label: 'Filial, manzil, telefon', hint: 'va nomdan keyingi matn', size: 1, bold: false },
  { key: 'no', label: 'Buyurtma raqami', hint: '', size: 2, bold: true },
  { key: 'info', label: 'Sana, turi, stol, kassir', hint: '', size: 1, bold: false },
  { key: 'items', label: 'Taomlar', hint: 'nomi, qo‘shimchalari, soni × narx', size: 1, bold: false },
  { key: 'sums', label: 'Jami, chegirma, to‘lov usuli, qaytim', hint: '', size: 1, bold: false },
  { key: 'total', label: 'Yakuniy summa (TO‘LOV)', hint: '', size: 2, bold: true },
  { key: 'footer', label: 'Chek osti', hint: '', size: 1, bold: false },
];

/** Sozlama kalitlari: restoran nomi — avvalgi kalitlar (receipt.name_size/name_bold) */
export function receiptStyleKeys(g: ReceiptGroup): { size: string; bold: string } {
  return { size: `receipt.${g}_size`, bold: `receipt.${g}_bold` };
}

export interface ReceiptDesign {
  /** Restoran nomidan oldingi xabar */
  top: string;
  nameSize: ReceiptSize;
  nameBold: boolean;
  /** Filial manzili (receipt.show_contacts) */
  showContacts: boolean;
  /** Filial telefoni (receipt.show_phone, Q-066) */
  showPhone: boolean;
  showNo: boolean;
  showTime: boolean;
  showStaff: boolean;
  /** Har guruh yozuvlarining kattaligi va qalinligi (Q-065) */
  styles: Record<ReceiptGroup, LineStyle>;
  /** Chek tepasida logotip (oq-qora, Q-068) */
  logo: MonoBitmap | null;
  logoSize: 'S' | 'M' | 'L';
  /** "Nomdan keyingi matn" yonidagi rasm va uning kattaligi (yozuv bo'yiga nisbatan, %) */
  headerIcon: MonoBitmap | null;
  headerIconSize: number;
  /** "Tepadagi xabar" yonidagi rasm va uning kattaligi (Q-069) */
  topIcon: MonoBitmap | null;
  topIconSize: number;
}

/** Matn yonidagi rasm kattaligi, % (50–250; yo'q yoki noto'g'ri — 100) */
const iconSize = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(250, Math.max(50, v)) : 100);

/** Chek ko'rinishi sozlamalari; yo'q yoki noto'g'ri qiymat — standart (hozirgi chek) */
export function readReceiptDesign(s: Record<string, unknown>): ReceiptDesign {
  const on = (k: string) => s[k] !== false;
  const styles = {} as Record<ReceiptGroup, LineStyle>;
  for (const g of RECEIPT_GROUPS) {
    const k = receiptStyleKeys(g.key);
    const size = Number(s[k.size]);
    const bold = s[k.bold];
    styles[g.key] = {
      size: size === 1 || size === 2 || size === 3 ? size : g.size,
      bold: typeof bold === 'boolean' ? bold : g.bold,
    };
  }
  return {
    top: typeof s['receipt.top'] === 'string' ? (s['receipt.top'] as string) : '',
    nameSize: styles.name.size,
    nameBold: styles.name.bold,
    showContacts: on('receipt.show_contacts'),
    showPhone: on('receipt.show_phone'),
    showNo: on('receipt.show_no'),
    showTime: on('receipt.show_time'),
    showStaff: on('receipt.show_staff'),
    styles,
    logo: on('receipt.show_logo') ? decodeMono(s['receipt.logo']) : null,
    logoSize: s['receipt.logo_size'] === 'S' || s['receipt.logo_size'] === 'L' ? (s['receipt.logo_size'] as 'S' | 'L') : 'M',
    headerIcon: decodeMono(s['receipt.header_icon']),
    headerIconSize: iconSize(s['receipt.header_icon_size']),
    topIcon: decodeMono(s['receipt.top_icon']),
    topIconSize: iconSize(s['receipt.top_icon_size']),
  };
}

/** Band stollar: stol -> eng eski ochiq buyurtma vaqti (stol shundan beri band) */
export function busySince(orders: readonly Pick<LocalOrder, 'table_id' | 'created_at_local'>[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const o of orders) {
    if (!o.table_id) continue;
    const prev = out.get(o.table_id);
    if (!prev || o.created_at_local < prev) out.set(o.table_id, o.created_at_local);
  }
  return out;
}

/** Zal ekranidagi stol holati (Q-056) */
export interface TableState {
  /** Stoldagi ochiq (to'lanmagan) buyurtmalar, eskisi birinchi */
  orders: LocalOrder[];
  /** Eng eski buyurtma vaqti */
  since: string;
  total: number;
  guests: number;
  /** Xizmat qilayotgan xodim(lar) */
  staff: string[];
  /** Stol egasi ofitsiant(lar) (Q-067): id */
  waiters: string[];
  /** Oshxonada tayyor taomi bor */
  ready: boolean;
  /** hall.long_minutes dan uzoq band */
  long: boolean;
  /** Hisob cheki chiqqan — mehmon to'lovni kutmoqda (Q-062) */
  bill: boolean;
  minutes: number;
  /** Boshqa ofitsiant stoli, "faqat o'ziniki" rejimida (Q-070): summa ko'rsatilmaydi */
  hidden: boolean;
}

/** Stollar holati: faqat band stollar qaytadi (bo'shi — yo'q) */
export function tableStates(
  orders: readonly LocalOrder[],
  readyOrderIds: ReadonlySet<string>,
  now: number,
  longMinutes: number,
): Map<string, TableState> {
  const out = new Map<string, TableState>();
  const open = orders
    .filter((o) => o.table_id && !o.is_paid && o.status !== 'voided')
    .sort((a, b) => a.created_at_local.localeCompare(b.created_at_local));
  for (const o of open) {
    const id = o.table_id!;
    const cur = out.get(id);
    const ready = Boolean(o.id && readyOrderIds.has(o.id));
    if (!cur) {
      const minutes = Math.max(0, (now - new Date(o.created_at_local).getTime()) / 60_000);
      out.set(id, {
        orders: [o],
        since: o.created_at_local,
        total: o.total,
        guests: o.guests ?? 0,
        staff: (o.waiter_name ?? o.staff_name) ? [(o.waiter_name ?? o.staff_name)!] : [],
        waiters: o.waiter_id ? [o.waiter_id] : [],
        ready,
        minutes,
        long: minutes >= longMinutes,
        bill: billPrinted(o),
        hidden: Boolean(o.hidden),
      });
    } else {
      cur.orders.push(o);
      cur.total += o.total;
      cur.guests += o.guests ?? 0;
      const who = o.waiter_name ?? o.staff_name;
      if (who && !cur.staff.includes(who)) cur.staff.push(who);
      if (o.waiter_id && !cur.waiters.includes(o.waiter_id)) cur.waiters.push(o.waiter_id);
      cur.ready = cur.ready || ready;
      cur.bill = cur.bill || billPrinted(o);
      cur.hidden = cur.hidden || Boolean(o.hidden);
    }
  }
  return out;
}

/** Band bo'lgan vaqt: "8 daq", "1 soat 12 daq" */
export function fmtBusy(minutes: number): string {
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m} daq`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} soat ${m % 60} daq` : `${h} soat`;
}

// ---------------------------------------------------------------------------
// Savdo kanali (Q-046): katalog shu kanal narxida — mahsulot, qo'shimcha, kombo ustamasi;
// kanalda sotilmaydigan mahsulot — "tugadi". Server (app.order_add_lines) bilan bir xil qoida.
// ---------------------------------------------------------------------------
export function channelBoot<B extends Pick<Boot, 'products' | 'modifier_groups' | 'combos'>>(boot: B, rule: ChannelRule | null | undefined): B {
  if (!rule) return boot;
  return {
    ...boot,
    products: boot.products.map((p) => ({
      ...p,
      price: channelUnit(rule, p.id, p.price),
      is_available: p.is_available && channelAvailable(rule, p.id),
    })),
    modifier_groups: boot.modifier_groups.map((g) => ({ ...g, modifiers: g.modifiers.map((m) => ({ ...m, price: channelMarkup(rule, m.price) })) })),
    combos: (boot.combos ?? []).map((c) => ({
      ...c,
      slots: c.slots.map((sl) => ({ ...sl, options: sl.options.map((o) => ({ ...o, extra_price: channelMarkup(rule, o.extra_price) })) })),
    })),
  };
}

// ---------------------------------------------------------------------------
// Band stol amallari (Q-060)
// ---------------------------------------------------------------------------
/** Amal qilish mumkin bo'lgan buyurtma: serverda bor, bekor qilinmagan, to'lov qilinmagan */
export function editable(o: LocalOrder): boolean {
  return Boolean(o.id) && o.sync === 'synced' && o.status !== 'voided' && !o.is_paid && o.paid === 0 && o.refunded === 0;
}

/** Bekor qilinmagan taomlar */
export function liveItems<T extends Pick<OrderItemJson, 'status'>>(items: readonly T[]): T[] {
  return items.filter((i) => i.status !== 'voided');
}

/** Taomni bekor qilish sabablari (tez tanlash) */
export const VOID_REASONS = ['Mehmon voz kechdi', 'Xato kiritildi', 'Uzoq kutdi', 'Sifati yomon', 'Tugab qolgan'] as const;
/** Butun buyurtmani bekor qilish sabablari — hisobotda sabablar bo'yicha yig'iladi */
export const ORDER_VOID_REASONS = ['Mehmon voz kechdi', 'Mehmon ketib qoldi', 'Xato kiritildi', 'Uzoq kutdi', 'Sifati yomon'] as const;

/** Bekor qilinadigan taomlardan nechtasi qaytadi / isrof (avtomatik usulda) */
export function voidSplit(items: readonly Pick<OrderItemJson, 'qty' | 'status'>[], mode: VoidStockMode): { back: number; lost: number } {
  let back = 0;
  let lost = 0;
  for (const i of items) {
    if (i.status === 'voided') continue;
    if (itemReturns(mode, i.status)) back += i.qty;
    else lost += i.qty;
  }
  return { back, lost };
}

/** Teng bo'lish: har kishidan qancha (oxirgisi — qolgan hammasi) */
export function equalParts(remaining: number, people: number): number[] {
  const n = Math.max(1, Math.floor(people));
  if (remaining <= 0) return [];
  const each = Math.ceil(remaining / n / 100) * 100;
  const out: number[] = [];
  let left = remaining;
  for (let i = 0; i < n && left > 0; i++) {
    const part = i === n - 1 ? left : Math.min(each, left);
    out.push(part);
    left -= part;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Ofitsiant (Q-067)
// ---------------------------------------------------------------------------
/** Buyurtma boshqa ofitsiantniki va unga tegish huquqi yo'q (server ham tekshiradi) */
export function otherWaiters(o: Pick<LocalOrder, 'waiter_id'>, me: string | null | undefined, canOthers: boolean): boolean {
  return Boolean(o.waiter_id && me && o.waiter_id !== me && !canOthers);
}

/** Stol "meniki": bo'sh emas va undagi biror buyurtma shu ofitsiantniki */
export function tableMine(st: Pick<TableState, 'waiters'> | undefined, me: string | null | undefined): boolean {
  return Boolean(st && me && st.waiters.includes(me));
}

/** Hisob cheki kassa printerida — ofitsiantga ko'rinadigan holat */
export function billPrintText(o: Pick<LocalOrder, 'bill_print'>): string | null {
  if (o.bill_print === 'pending') return 'Hisob cheki kassaga yuborildi — chiqishini kuting';
  if (o.bill_print === 'printed') return 'Hisob cheki kassada chiqdi';
  if (o.bill_print === 'failed') return 'Hisob cheki kassada chiqmadi — qayta yuboring yoki kassirga ayting';
  return null;
}
